import React from 'react';
import { createPortal } from 'react-dom';
import { useTranslation } from '@rbx/intl';
import { Alert } from '@rbx/ui';
import { AlertColor } from '@mui/material';
import RiskLevel from '../../../types/RiskLevel';

interface RiskLevelAlertPortalProps {
  container: HTMLDivElement | null;
  riskLevel: RiskLevel;
}

/**
 * Gets the Alert severity level based on risk level
 */
const getRiskLevelSeverity = (riskLevel: RiskLevel): AlertColor => {
  switch (riskLevel) {
    case RiskLevel.LOW:
      return 'info';
    case RiskLevel.MEDIUM:
      return 'warning';
    case RiskLevel.HIGH:
    default:
      return 'error';
  }
};

/**
 * Gets the Alert message based on risk level
 */
const getRiskLevelMessage = (riskLevel: RiskLevel, translate: (key: string) => string): string => {
  switch (riskLevel) {
    case RiskLevel.LOW:
      return translate('Description.TryItOutLowRisk');
    case RiskLevel.MEDIUM:
      return translate('Description.TryItOutMediumRisk');
    case RiskLevel.HIGH:
    default:
      return translate('Description.TryItOutHighRisk');
  }
};

/**
 * Portal component for risk level alerts
 */
const RiskLevelAlertPortal = ({
  container,
  riskLevel,
}: RiskLevelAlertPortalProps): React.ReactElement | null => {
  const { translate } = useTranslation();
  if (!container) return null;

  return (
    <React.Fragment key='risk-level-alert'>
      {createPortal(
        <Alert
          severity={getRiskLevelSeverity(riskLevel)}
          style={{
            alignItems: 'center',
          }}>
          <div>{getRiskLevelMessage(riskLevel, translate)}</div>
          <div>{translate('Description.TryItOutSecurityReminder')}</div>
        </Alert>,
        container,
      )}
    </React.Fragment>
  );
};

export default RiskLevelAlertPortal;
